import { EResourceStatus, EResourceType, ResourceRegionType } from '#shared';

import { useQuerySurgeons, useQuerySurgeonsRegions } from '../api';
import { subRegionMap } from '../helpers';

import type { StaffInfo } from '#entities';

export const useRegionedSurgeonGroups = (): Record<
  ResourceRegionType,
  StaffInfo[][]
> => {
  const { data: regions = [] } = useQuerySurgeonsRegions();
  const { data: surgeons } = useQuerySurgeons();

  const regionBuckets: Partial<Record<ResourceRegionType, StaffInfo[][]>> = {
    [ResourceRegionType.West]: [],
    [ResourceRegionType.East]: [],
    [ResourceRegionType.North]: [],
    [ResourceRegionType.South]: [],
  };

  // Map each hubId to raw region name like 'West', 'Northeast', etc.
  const hubIdToRegion: Record<string, string> = {};
  regions.forEach(region =>
    region.hubs?.forEach(hub => {
      if (hub.id && region.name) {
        hubIdToRegion[hub.id] = region.name;
      }
    }),
  );

  const rawBuckets: Record<string, StaffInfo[]> = {};

  for (const surgeon of surgeons) {
    const rawRegion = surgeon.hubId && hubIdToRegion[surgeon.hubId];
    if (!rawRegion) {
      continue;
    }

    const info: StaffInfo = {
      id: surgeon.id,
      teamMemberType: EResourceType.Surgeon,
      // Keep the `?.` even though the type says non-null: a lagging NOP may
      // still send organs: null, which the surgeon schema only warns about
      // (the raw value reaches consumers unchanged).
      countsInfo: surgeon.organs?.map(organ => ({
        id: surgeon.id,
        organType: organ,
        availableCount: surgeon.status === EResourceStatus.Available ? 1 : 0,
        assignedCount: surgeon.status === EResourceStatus.Assigned ? 1 : 0,
        availableTrialCount: 0,
        assignedTrialCount: 0,
      })),
      details: [],
    };

    rawBuckets[rawRegion] ??= [];
    rawBuckets[rawRegion].push(info);
  }

  for (const [regionType, subRegions] of Object.entries(subRegionMap)) {
    regionBuckets[regionType as ResourceRegionType] = subRegions.map(
      sr => rawBuckets[sr] ?? [],
    );
  }

  return regionBuckets as Record<ResourceRegionType, StaffInfo[][]>;
};
